import type { ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import { TONE_CLASS, TONE_DOT, type StateLabel } from '../../lib/agents'

export function Panel({ Icon, title, aside, children, className = '' }: {
  Icon?: LucideIcon
  title: string
  aside?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <section className={`rounded-2xl border border-gray-300 bg-white shadow-sm ${className}`}>
      <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-5 py-4">
        <div className="flex min-w-0 items-center gap-2">
          {Icon && <Icon size={18} className="shrink-0 text-primary-600" />}
          <h2 className="truncate font-bold text-gray-900">{title}</h2>
        </div>
        {aside && <div className="shrink-0 whitespace-nowrap">{aside}</div>}
      </div>
      {children}
    </section>
  )
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="px-5 py-8 text-sm text-gray-600">{children}</div>
}

/** Shown in place of a section whose data source could not be read. */
export function Unavailable({ children }: { children: ReactNode }) {
  return <div role="alert" className="px-5 py-8 text-sm text-red-700">{children}</div>
}

export function StateBadge({ state }: { state: StateLabel }) {
  return (
    <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${TONE_CLASS[state.tone]}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${TONE_DOT[state.tone]}`} />
      {state.label}
    </span>
  )
}

export function Stat({ label, value, detail, Icon, tone = 'neutral' }: {
  label: string
  value: ReactNode
  detail: string
  Icon: LucideIcon
  tone?: 'neutral' | 'warn' | 'alert'
}) {
  const ring = tone === 'alert' ? 'border-red-200 bg-red-50/50' : tone === 'warn' ? 'border-amber-200 bg-amber-50/50' : 'border-gray-300 bg-white'
  const iconColor = tone === 'alert' ? 'text-red-500' : tone === 'warn' ? 'text-amber-500' : 'text-gray-600'
  return (
    <div className={`rounded-2xl border p-4 shadow-sm ${ring}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-gray-600">{label}</span>
        <Icon size={17} className={iconColor} />
      </div>
      <div className="mt-2 text-2xl font-bold text-gray-900">{value}</div>
      <div className="mt-1 line-clamp-2 text-xs text-gray-600">{detail}</div>
    </div>
  )
}
